import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { api, formatApiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, ShieldCheck, User, Store, CreditCard, Banknote,
  Receipt, Wallet, AlertCircle, Copy as CopyIcon, CheckCircle2, XCircle, RotateCcw,
} from "lucide-react";

const Field = ({ label, value, mono = false, className = "", copy = false }) => {
  const display = value == null || value === "" ? null : value;
  return (
    <div className={className} data-testid={`field-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-0.5 text-sm flex items-center gap-1.5 ${mono ? "font-mono" : ""}`}>
        {display != null ? display : <em className="text-muted-foreground">—</em>}
        {copy && display && (
          <button
            type="button" title="Copy"
            onClick={() => { navigator.clipboard.writeText(String(display)); toast.success("Copied"); }}
            className="text-muted-foreground hover:text-foreground"
          >
            <CopyIcon className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
};

const SectionCard = ({ title, icon: Icon, children, action }) => (
  <div className="surface-card p-5 sm:p-6">
    <div className="flex items-center justify-between mb-4">
      <div className="font-display text-lg font-semibold flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-primary" />} {title}
      </div>
      {action}
    </div>
    {children}
  </div>
);

const moneyOrDash = (v) => (v == null ? "—" : formatINR(v));
const fmtDate = (s) => (s ? new Date(s).toLocaleString() : "—");

export default function AdminApprovalDetail() {
  const { txnId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null); // "approve" | "reject" | "request_resubmit"
  const [remarks, setRemarks] = useState("");
  const [pw, setPw] = useState("");
  const [acting, setActing] = useState(false);

  const role = (user?.role || "").toLowerCase();
  const isAdmin = role === "admin" || role === "approver_all";
  const myLevel = ["approver_l1", "approver_l2", "approver_l3"].includes(role)
    ? parseInt(role.split("_l")[1], 10)
    : null;

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/approvals/${txnId}/detail`);
      setD(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load approval detail");
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [txnId]);

  const submitAction = async () => {
    if (!action) return;
    if (action !== "approve" && !remarks.trim()) {
      toast.error("Remarks required for reject / request resubmit");
      return;
    }
    if (!pw.trim()) { toast.error("Password required to confirm"); return; }
    setActing(true);
    try {
      const txnRealId = d?.transaction?.id || txnId;
      const { data } = await api.post(`/admin/approvals/${txnRealId}/action`, {
        action,
        remarks: remarks.trim() || undefined,
        password: pw,
      });
      toast.success(
        action === "approve" ? `Approved · status now ${data.status}` :
        action === "reject" ? "Transaction rejected — wallet refunded" :
        "Resubmission requested — wallet refunded"
      );
      setAction(null); setRemarks(""); setPw("");
      await reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Action failed");
    } finally { setActing(false); }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="container mx-auto py-12 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin mr-2 text-muted-foreground" />
          <span className="text-muted-foreground">Loading approval detail…</span>
        </div>
      </DashboardLayout>
    );
  }
  if (!d) {
    return (
      <DashboardLayout>
        <div className="container mx-auto py-12 text-center text-muted-foreground">
          Approval not found.
          <div className="mt-4">
            <Button variant="outline" onClick={() => navigate("/admin/approvals")}>Back to queue</Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const t = d.transaction || {};
  const partner = d.partner || {};
  const customer = d.customer || {};
  const pmode = d.pay_mode || {};
  const instr = d.instrument || {};
  const isPayout = t.type === "payout";

  // Action availability: must be a pending_lN row AND this approver matches the level (admins always)
  const txnLevel = t.status?.startsWith("pending_l") ? Number(t.status.split("_l")[1]) : null;
  const canAct = !!txnLevel && (isAdmin || txnLevel === myLevel);

  // Previous-level approver remarks — extracted from approval_chain so reviewers
  // at L2/L3 see what L1/L2 said WITHOUT flipping to the chain tab.
  const priorApprovals = Array.isArray(t.approval_chain)
    ? t.approval_chain
        .filter((e) => (e?.event || e?.action || "").toLowerCase().includes("approve") && (e?.note || e?.remarks))
        .map((e) => ({
          level: e.level || (e.event || "").match(/l(\d)/i)?.[1] || "?",
          by: e.by_email || e.approver_email || e.approver_id || e.by || "—",
          at: e.at || e.timestamp,
          note: e.note || e.remarks,
        }))
    : [];

  return (
    <DashboardLayout>
      <div className="container mx-auto py-8 px-4">
        <button
          onClick={() => navigate("/admin/approvals")}
          className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-3"
          data-testid="back-to-approvals"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to approvals
        </button>

        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <span className="overline">{t.type?.toUpperCase()} · {t.category || t.rail}</span>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 font-mono">
              {moneyOrDash(t.amount)}
            </h1>
            <div className="text-sm text-muted-foreground mt-1 font-mono">{t.display_id || t.id}</div>
          </div>
          <div className="flex gap-2 flex-wrap items-start">
            <Badge variant="outline" className="text-xs">{t.status}</Badge>
            <Badge variant="outline" className="text-xs">{partner?.business_name || partner?.full_name || "—"}</Badge>
          </div>
        </div>

        {/* Action bar — only when txn is at this approver's pending level */}
        {canAct ? (
          <div className="mt-6 surface-card p-4 border-2 border-primary/30">
            {priorApprovals.length > 0 && (
              <div className="mb-3 pb-3 border-b border-border space-y-1.5">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                  Prior approvals — read before you act
                </div>
                {priorApprovals.map((p, i) => (
                  <div
                    key={i}
                    className="text-xs flex items-start gap-2"
                    data-testid={`prior-approval-l${p.level}`}
                  >
                    <Badge variant="outline" className="text-[10px] font-mono shrink-0 mt-0.5">
                      L{p.level}
                    </Badge>
                    <div className="min-w-0">
                      <div className="font-medium truncate">
                        {p.by}
                        {p.at && (
                          <span className="text-muted-foreground font-mono ml-1">
                            · {new Date(p.at).toLocaleString()}
                          </span>
                        )}
                      </div>
                      <div className="text-muted-foreground italic mt-0.5 break-words">
                        "{p.note}"
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mr-2">
                Act on {t.status}:
              </div>
              <Button
                size="sm" variant="default"
                className="bg-success hover:bg-success/90"
                onClick={() => { setAction("approve"); setRemarks(""); setPw(""); }}
                data-testid="detail-approve"
              >
                <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
              </Button>
              <Button
                size="sm" variant="outline"
                className="border-secondary/40 text-secondary hover:bg-secondary/10"
                onClick={() => { setAction("request_resubmit"); setRemarks(""); setPw(""); }}
                data-testid="detail-resubmit"
              >
                <RotateCcw className="h-4 w-4 mr-1" /> Request resubmit
              </Button>
              <Button
                size="sm" variant="outline"
                className="border-destructive/40 text-destructive hover:bg-destructive/10"
                onClick={() => { setAction("reject"); setRemarks(""); setPw(""); }}
                data-testid="detail-reject"
              >
                <XCircle className="h-4 w-4 mr-1" /> Reject
              </Button>
              <div className="text-[10px] text-muted-foreground ml-auto">
                {isAdmin ? "Admin override — any level" : `Approver L${myLevel}`}
              </div>
            </div>
          </div>
        ) : t.status?.startsWith("pending_l") ? (
          <div className="mt-4 text-xs text-muted-foreground italic">
            This transaction is at <span className="font-mono font-semibold">{t.status}</span> — your role can't act at this level.
          </div>
        ) : null}

        <Tabs defaultValue="overview" className="mt-8">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="partner" data-testid="tab-partner">Partner</TabsTrigger>
            <TabsTrigger value="customer" data-testid="tab-customer">Customer</TabsTrigger>
            <TabsTrigger value="instrument" data-testid="tab-instrument">Instrument</TabsTrigger>
            <TabsTrigger value="raw" data-testid="tab-raw">PG Response</TabsTrigger>
            <TabsTrigger value="chain" data-testid="tab-chain">Approval Chain</TabsTrigger>
          </TabsList>

          {/* OVERVIEW — money math + key txn fields */}
          <TabsContent value="overview" className="mt-6 space-y-6">
            <SectionCard title="Money breakdown" icon={Receipt}>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                <Field label="Txn amount" value={moneyOrDash(t.amount)} mono />
                <Field label="Partner charge" value={moneyOrDash(t.partner_charge ?? t.platform_fee)} mono />
                <Field label="PG / Provider fee" value={moneyOrDash(t.pg_fee ?? t.mdr_fee)} mono />
                <Field label="Settlement fee" value={moneyOrDash(t.settle_fee)} mono />
                <Field label="Net payout amount" value={moneyOrDash(t.payout_amount ?? t.net_amount)} mono />
                <Field label="Commission earned" value={moneyOrDash(t.commission_amount)} mono />
                <Field label="Wallet debited (partner)" value={moneyOrDash(t.partner_wallet_debit)} mono />
                <Field label="Wallet debited (customer)" value={moneyOrDash(t.customer_wallet_debit)} mono />
              </div>
            </SectionCard>

            <SectionCard title="Txn details" icon={Receipt}>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                <Field label="Type" value={t.type} />
                <Field label="Status" value={t.status} />
                <Field label="Category / Rail" value={t.category || t.rail} />
                <Field label="Funded by" value={t.funded_from || t.gateway} />
                <Field label="Provider" value={t.provider} />
                <Field label="Provider ref" value={t.provider_ref} mono copy />
                <Field label="Created" value={fmtDate(t.created_at)} />
                <Field label="Updated" value={fmtDate(t.updated_at)} />
                <Field label="Customer mobile" value={t.customer_mobile} mono />
                <Field label="Beneficiary nickname" value={t.beneficiary_nickname || instr.nickname} className="sm:col-span-2 lg:col-span-2" />
                <Field label="Message" value={t.message} className="sm:col-span-2 lg:col-span-2" />
              </div>
            </SectionCard>
          </TabsContent>

          {/* PARTNER */}
          <TabsContent value="partner" className="mt-6 space-y-6">
            <SectionCard
              title="Partner profile"
              icon={Store}
              action={partner?.id && (
                <Button size="sm" variant="outline" onClick={() => navigate(`/admin/users?focus=${partner.id}`)}>
                  View partner
                </Button>
              )}
            >
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                <Field label="Business name" value={partner.business_name} />
                <Field label="Full name" value={partner.full_name} />
                <Field label="Email" value={partner.email} mono />
                <Field label="Mobile" value={partner.mobile} mono />
                <Field label="Role" value={partner.role} />
                <Field label="Package" value={partner.package_id || partner.package} />
                <Field label="Wallet balance" value={moneyOrDash(partner.wallet_balance)} mono />
                <Field label="Joined" value={fmtDate(partner.created_at)} />
              </div>
            </SectionCard>
            <SectionCard title="Partner KYC" icon={ShieldCheck}>
              {!d.partner_kyc ? (
                <div className="text-sm text-muted-foreground">No KYC on file for this partner.</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  <Field label="Status" value={d.partner_kyc.status} />
                  <Field label="PAN" value={d.partner_kyc.pan} mono />
                  <Field label="Aadhaar (masked)" value={d.partner_kyc.aadhaar_masked || d.partner_kyc.aadhaar} mono />
                  <Field label="DOB" value={d.partner_kyc.dob} />
                  <Field label="GSTIN" value={d.partner_kyc.gstin} mono />
                  <Field label="Address" value={d.partner_kyc.address} className="sm:col-span-2 lg:col-span-3" />
                  <Field
                    label="Reviewed by"
                    value={d.partner_kyc.reviewer ? `${d.partner_kyc.reviewer.full_name} (${d.partner_kyc.reviewer.email})` : "—"}
                    className="sm:col-span-2 lg:col-span-2"
                  />
                  <Field label="Reviewed at" value={fmtDate(d.partner_kyc.reviewed_at)} />
                </div>
              )}
            </SectionCard>
          </TabsContent>

          {/* CUSTOMER */}
          <TabsContent value="customer" className="mt-6 space-y-6">
            {!t.customer_mobile && !customer.mobile ? (
              <div className="surface-card p-6 text-sm text-muted-foreground">
                <User className="h-4 w-4 inline mr-1 opacity-50" /> No customer linked (self-mode payout / partner-funded BBPS).
              </div>
            ) : (
              <>
                <SectionCard title="Customer wallet" icon={Wallet} action={
                  <Button size="sm" variant="outline" onClick={() => navigate(`/admin/customers?focus=${customer.mobile || t.customer_mobile}`)}>
                    Open customer
                  </Button>
                }>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    <Field label="Mobile" value={customer.mobile || t.customer_mobile} mono />
                    <Field label="Balance" value={moneyOrDash(customer.balance)} mono />
                    <Field label="Name (from KYC)" value={d.customer_kyc?.full_name} />
                    <Field label="Wallet created" value={fmtDate(customer.created_at)} />
                  </div>
                </SectionCard>
                <SectionCard title="Customer KYC" icon={ShieldCheck}>
                  {!d.customer_kyc ? (
                    <div className="text-sm text-muted-foreground">No KYC on file for this customer.</div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                      <Field label="Status" value={d.customer_kyc.status} />
                      <Field label="Full name" value={d.customer_kyc.full_name} />
                      <Field label="PAN" value={d.customer_kyc.pan} mono />
                      <Field label="Aadhaar (masked)" value={d.customer_kyc.aadhaar_masked || d.customer_kyc.aadhaar} mono />
                      <Field label="DOB" value={d.customer_kyc.dob} />
                      <Field label="Email" value={d.customer_kyc.email} mono />
                      <Field label="Address" value={d.customer_kyc.address} className="sm:col-span-2 lg:col-span-3" />
                      <Field
                        label="Performed by"
                        value={d.customer_kyc.performed_by_user
                          ? `${d.customer_kyc.performed_by_user.full_name} (${d.customer_kyc.performed_by_user.role})`
                          : "—"}
                        className="sm:col-span-2 lg:col-span-2"
                      />
                      <Field label="Performed at" value={fmtDate(d.customer_kyc.created_at)} />
                    </div>
                  )}
                </SectionCard>
              </>
            )}
          </TabsContent>

          {/* INSTRUMENT — bene / card / payment link */}
          <TabsContent value="instrument" className="mt-6 space-y-6">
            <SectionCard title={isPayout ? "Payout beneficiary" : "BBPS consumer account"} icon={isPayout ? Banknote : CreditCard}>
              {!d.instrument ? (
                <div className="text-sm text-muted-foreground">No instrument linked.</div>
              ) : isPayout ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  <Field label="Nickname" value={instr.nickname} />
                  <Field label="Type" value={instr.type} />
                  <Field label="Holder name" value={instr.account_holder_name} />
                  <Field label="Account number" value={instr.account_number} mono copy />
                  <Field label="IFSC" value={instr.ifsc} mono />
                  <Field label="UPI ID" value={instr.upi_id} mono />
                  <Field label="Provider bene ID" value={instr.provider_bene_id} mono copy />
                  <Field label="Approval status" value={instr.approval_status} />
                  <Field label="Created" value={fmtDate(instr.created_at)} />
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  <Field label="Category" value={instr.category} />
                  <Field label="Nickname" value={instr.nickname} />
                  <Field label="Biller" value={instr.biller_name} />
                  <Field label="Consumer ID" value={instr.consumer_id} mono copy />
                  <Field label="Consumer name" value={instr.consumer_name} />
                  <Field label="Last bill ₹" value={moneyOrDash(instr.last_bill_amount)} mono />
                  <Field label="Due date" value={instr.last_bill_due_date} />
                  <Field label="Created" value={fmtDate(instr.created_at)} />
                </div>
              )}
            </SectionCard>
            <SectionCard title="Pay mode (whitelist row that funded this)" icon={CreditCard}>
              {!d.pay_mode ? (
                <div className="text-sm text-muted-foreground">No pay-mode row linked — txn was funded from existing wallet balance.</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  <Field label="Mode type" value={pmode.mode_type} />
                  <Field label="Mode detail" value={pmode.mode_detail} mono />
                  <Field label="Status" value={pmode.status} />
                  <Field label="Approved by" value={pmode.approved_by} mono />
                  <Field label="Approved at" value={fmtDate(pmode.approved_at)} />
                  <Field label="Created" value={fmtDate(pmode.created_at)} />
                </div>
              )}
            </SectionCard>
          </TabsContent>

          {/* RAW PG response */}
          <TabsContent value="raw" className="mt-6">
            <SectionCard title="Raw provider / PG response" icon={Receipt}>
              <pre
                className="text-[11px] font-mono whitespace-pre-wrap break-all bg-muted/40 p-4 rounded border border-border max-h-[60vh] overflow-auto"
                data-testid="raw-pg-json"
              >
                {JSON.stringify(t.provider_response || t.raw_response || t.last_status_response || t, null, 2)}
              </pre>
            </SectionCard>
          </TabsContent>

          {/* APPROVAL CHAIN */}
          <TabsContent value="chain" className="mt-6 space-y-3">
            <SectionCard title="Approval timeline" icon={ShieldCheck}>
              {Array.isArray(t.approval_chain) && t.approval_chain.length > 0 ? (
                <ol className="space-y-3">
                  {t.approval_chain.map((ev, idx) => (
                    <li key={idx} className="border-l-2 border-primary/30 pl-4 ml-2 relative">
                      <div className="absolute -left-[7px] top-0.5 w-3 h-3 rounded-full bg-primary" />
                      <div className="text-xs font-semibold">{ev.event || ev.action}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {ev.at ? fmtDate(ev.at) : ""} · {ev.by_email || ev.approver_id || ev.by || "—"}
                      </div>
                      {(ev.note || ev.reason || ev.message) && (
                        <div className="text-xs mt-1 italic text-muted-foreground">"{ev.note || ev.reason || ev.message}"</div>
                      )}
                    </li>
                  ))}
                </ol>
              ) : (
                <div className="text-sm text-muted-foreground">No approval events yet.</div>
              )}
            </SectionCard>
          </TabsContent>
        </Tabs>

        <div className="mt-8 rounded-md border border-primary/20 bg-primary/5 p-3 text-xs flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-primary mt-0.5" />
          <div>
            <span className="font-semibold">Approval actions confirmed above.</span> All actions advance the
            txn through L1→L2→L3 (or refund on reject/resubmit) and write to the audit chain.
          </div>
        </div>
      </div>

      {/* Action confirm dialog */}
      <Dialog open={!!action} onOpenChange={(o) => !o && setAction(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="capitalize">
              {action === "request_resubmit" ? "Request resubmit" : action} · {t.display_id || t.id?.slice(0, 8)}
            </DialogTitle>
            <DialogDescription>
              {action === "approve"
                ? "Advance to next level. On L3 the provider call fires immediately."
                : action === "reject"
                ? "Final rejection — wallet is refunded to the partner immediately."
                : "Refunds the wallet and lets the partner edit + resubmit the txn."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {action !== "approve" && (
              <div>
                <Label className="text-xs">
                  Remarks <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  value={remarks} onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Required — explain why."
                  rows={3} className="mt-1 text-sm"
                  data-testid="action-remarks"
                />
              </div>
            )}
            {action === "approve" && (
              <div>
                <Label className="text-xs">Note (optional)</Label>
                <Textarea
                  value={remarks} onChange={(e) => setRemarks(e.target.value)}
                  rows={2} className="mt-1 text-sm"
                  placeholder="Any verification notes."
                  data-testid="action-remarks"
                />
              </div>
            )}
            <div>
              <Label className="text-xs">
                Your password <span className="text-destructive">*</span>
              </Label>
              <Input
                type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                placeholder="Required to confirm" className="mt-1 font-mono"
                data-testid="action-password"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Authenticated via your admin/approver password (audit trail).
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAction(null)} disabled={acting}>Cancel</Button>
            <Button
              onClick={submitAction}
              disabled={acting}
              className={
                action === "approve" ? "bg-success hover:bg-success/90" :
                action === "reject" ? "bg-destructive hover:bg-destructive/90" : ""
              }
              data-testid="action-confirm"
            >
              {acting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Confirm {action === "request_resubmit" ? "resubmit" : action}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
